import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { AppConstants } from 'src/app/shared/constants/app.contants';
import { environment } from 'src/environments/environment';
import { lastValueFrom } from 'rxjs';

export type DialogMode =
  | 'add-category' | 'edit-category'
  | 'add-subcategory' | 'edit-subcategory'
  | 'add-subsubcategory' | 'edit-subsubcategory';

@Component({
  selector: 'app-category-dialog',
  templateUrl: './category-dialog.component.html',
  styleUrls: ['./category-dialog.component.scss']
})
export class CategoryDialogComponent implements OnInit {
  form!: FormGroup;

  get mode(): DialogMode { return this.data.mode; }
  get isEdit(): boolean { return this.mode.startsWith('edit'); }

  get dialogTitle(): string {
    const map: Record<DialogMode, string> = {
      'add-category': 'Add Category',
      'edit-category': 'Edit Category',
      'add-subcategory': 'Add Sub Category',
      'edit-subcategory': 'Edit Sub Category',
      'add-subsubcategory': 'Add Sub Sub Category',
      'edit-subsubcategory': 'Edit Sub Sub Category'
    };
    return map[this.mode];
  }

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<CategoryDialogComponent>,
    private _loaderService: LoaderService,
    private apiService: ApiService,
    private toasterService: ToasterService,
    @Inject(MAT_DIALOG_DATA) public data: {
      mode: DialogMode;
      item?: any;
      parentId?: string;
    }
  ) {}

  ngOnInit(): void {
    this.buildForm();
    if (this.isEdit) {
      this.disableIdentifierFieldsForEdit();
    }
  }

  private buildForm(): void {
    const item = this.data.item;

    switch (this.mode) {
      case 'add-category':
        this.form = this.fb.group({
          categoryId: ['', Validators.required],
          description: ['']
        });
        break;

      case 'edit-category':
        this.form = this.fb.group({
          categoryId: [item?.categoryId || '', Validators.required],
          description: [item?.description || '']
        });
        break;

      case 'add-subcategory':
        this.form = this.fb.group({
          subCategoryId: ['', Validators.required],
          categoryId: [{ value: this.data.parentId || '', disabled: true }],
          description: ['']
        });
        break;

      case 'edit-subcategory':
        this.form = this.fb.group({
          subCategoryId: [item?.subCategoryId || '', Validators.required],
          categoryId: [{ value: item?.categoryId || '', disabled: true }],
          description: [item?.description || '']
        });
        break;

      case 'add-subsubcategory':
        this.form = this.fb.group({
          subSubCategoryId: ['', Validators.required],
          subCategoryId: [{ value: this.data.parentId || '', disabled: true }],
          description: ['']
        });
        break;

      case 'edit-subsubcategory':
        this.form = this.fb.group({
          subSubCategoryId: [item?.subSubCategoryId || '', Validators.required],
          subCategoryId: [{ value: item?.subCategoryId || '', disabled: true }],
          description: [item?.description || '']
        });
        break;
    }
  }

  private disableIdentifierFieldsForEdit(): void {
    switch (this.mode) {
      case 'edit-category':
        this.form.get('categoryId')?.disable({ emitEvent: false });
        break;
      case 'edit-subcategory':
        this.form.get('subCategoryId')?.disable({ emitEvent: false });
        break;
      case 'edit-subsubcategory':
        this.form.get('subSubCategoryId')?.disable({ emitEvent: false });
        break;
    }
  }

  isFieldInvalid(name: string): boolean {
    const ctrl = this.form.get(name);
    return !!(ctrl?.invalid && ctrl?.touched);
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  async onSave(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toasterService.showError('Please fill in all required fields', 'Validation Error');
      return;
    }

    const rawValue = this.form.getRawValue();
    const item = this.data.item;

    const endpointMap: Record<string, string> = {
      'add-category': 'categories',
      'edit-category': 'categories',
      'add-subcategory': 'subcategories',
      'edit-subcategory': 'subcategories',
      'add-subsubcategory': 'subsubcategories',
      'edit-subsubcategory': 'subsubcategories'
    };

    const endpoint = endpointMap[this.mode];
    const baseUrl = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.Lookups}/${endpoint}`;

    this._loaderService.showLoader();
    try {
      let response: any;

      if (this.isEdit) {
        const updatePayload = {
          id: item?.id,
          ...rawValue
        };
        response = await lastValueFrom(this.apiService.putByUrl(`${baseUrl}/${item?.id}`, updatePayload));
      } else {
        response = await lastValueFrom(this.apiService.postByUrl(baseUrl, rawValue));
      }

      if (response && (response.isSuccess || response.IsSuccess || response.id || response.ID || response.data !== undefined)) {
        this.toasterService.showSuccess(`${this.dialogTitle} saved successfully`, 'Success');
        this.dialogRef.close(true);
      } else {
        const msg = response?.errorMessageEn || response?.ErrorMessageEn || 'Operation failed';
        this.toasterService.showError(msg, 'Error');
      }
    } catch (error: any) {
      const msg = error?.error?.errorMessageEn || error?.message || 'An unexpected error occurred';
      this.toasterService.showError(msg, 'Error');
      console.error('[Category Dialog] Error:', error);
    } finally {
      this._loaderService.hideLoader();
    }
  }
}
